import { useNavigate } from 'react-router-dom'
import { useList } from '../lib/useList.ts'
import { formatDate } from '../lib/format.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { useState } from 'react'
import { ApiError, del } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Empty, Input, SkeletonRows } from '../components/ui.tsx'
import { IconEdit, IconEye, IconPlus, IconSearch, IconTrash } from '../components/icons.tsx'

type PatientRow = {
  id: string; mrn: string; fullName: string; fatherName: string | null
  age: number | null; sex: string | null; phone: string | null
  district: string | null; createdAt: string
}

// Demographics from the printed form. The NDPS register, the 30-day vitals
// series and the signed declaration are separate entities, still to come.
export function PatientsPage() {
  const list = useList<PatientRow>('/api/patients')
  const navigate = useNavigate()
  const { can } = useAuth()
  const toast = useToast()
  const [removing, setRemoving] = useState<PatientRow | null>(null)
  const writable = can('patient:write')

  return (
    <>
      <PageHeader title="Patients">
        {can('patient:write') ? (
          <Button variant="primary" onClick={() => navigate('/register')}>
            <IconPlus /> New registration
          </Button>
        ) : null}
      </PageHeader>
      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>
          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input
                placeholder="Search by name, phone or MRN"
                value={list.q}
                onChange={e => list.setQ(e.target.value)}
                aria-label="Search patients"
              />
            </div>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>MRN</th><th>Name</th><th>Father's name</th>
                    <th>Age</th><th>Phone</th><th>District</th><th>Registered</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={6} cols={8} /> : null}
                  {!list.loading && list.items.map(p => (
                    <tr key={p.id}>
                      <td className="mono">{p.mrn}</td>
                      <td style={{ fontWeight: 550 }}>{p.fullName}</td>
                      <td className="muted">{p.fatherName ?? '—'}</td>
                      <td>{p.age ?? '—'}</td>
                      <td className="muted">{p.phone ?? '—'}</td>
                      <td className="muted">{p.district ?? '—'}</td>
                      <td className="dim">{formatDate(p.createdAt)}</td>
                      <td className="actions">
                        <span className="row-actions">
                          <button className="icon-btn" title="View"
                            aria-label={`View ${p.fullName}`}
                            onClick={() => navigate(`/patients/${p.id}`)}>
                            <IconEye />
                          </button>
                          <button className="icon-btn" title="Edit"
                            aria-label={`Edit ${p.fullName}`} disabled={!writable}
                            onClick={() => navigate(`/patients/${p.id}/edit`)}>
                            <IconEdit />
                          </button>
                          <button className="icon-btn danger" title="Delete"
                            aria-label={`Delete ${p.fullName}`} disabled={!writable}
                            onClick={() => setRemoving(p)}>
                            <IconTrash />
                          </button>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title={list.q ? 'No matches' : 'No patients yet'}>
                {list.q
                  ? 'Try a different name, phone number or MRN.'
                  : 'Register the first patient from a completed paper form.'}
              </Empty>
            ) : null}

            {list.error ? (
              <div className="empty">
                <p style={{ color: 'var(--danger)' }}>{list.error}</p>
                <Button onClick={list.refresh} style={{ marginTop: 'var(--s-3)' }}>Try again</Button>
              </div>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}
        </div>
      </div>

      {removing ? (
        <ConfirmDialog
          title={`Delete ${removing.fullName}?`}
          body={`The record is kept, not erased — ${removing.mrn} stays issued and the audit trail keeps pointing at it. It stops appearing in this list.`}
          confirmLabel="Delete"
          danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await del(`/api/patients/${removing.id}`)
              list.refresh()
              toast.ok('Patient deleted')
            } catch (e) { toast.fail('Could not delete', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}
